<template>
  <v-card class="point-explorer-filters-card mb-4">
    <v-card-title class="pb-2">
      <v-icon left>mdi-filter</v-icon>
      Filters
    </v-card-title>
    
    <!-- Load Saved Query -->
    <v-card-text>
      <v-row>
        <v-col cols="12" md="6" lg="4">
          <div class="filter-field">
            <HaqSelect
              v-model="selectedSavedQuery"
              :items="savedQueryOptions"
              item-text="name"
              item-value="objectId"
              label="Load Saved Filters"
              clearable
              hide-details
              :loading="loadingSavedQueries"
              @change="onLoadQuery"
              :menu-props="{ bottom: true, offsetY: true, closeOnClick: true, closeOnContentClick: true, auto: true, maxWidth: 400 }"
            />
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Filters -->
    <v-card-text>
      <v-row>
        <!-- Site Filter -->
        <v-col cols="12" md="6" lg="4">
          <div class="filter-field">
            <HaqAutocomplete
              v-model="filters.siteId"
              :items="siteOptions"
              item-text="name"
              item-value="objectId"
              label="Site (Required)"
              clearable
              hide-details
              :loading="loadingSites"
              @change="onSiteChange"
              :menu-props="{ bottom: true, offsetY: true, closeOnClick: true, closeOnContentClick: true, auto: true, maxWidth: 400, maxHeight: 320 }"
            />
          </div>
        </v-col>
        
        <!-- Zone Filter -->
        <v-col cols="12" md="6" lg="4">
          <div class="filter-field">
            <HaqAutocomplete
              v-model="filters.zoneId"
              :items="zoneOptions"
              item-text="name"
              item-value="objectId"
              label="Zone"
              clearable
              hide-details
              :disabled="!filters.siteId"
              :loading="loadingZones"
              :menu-props="{ bottom: true, offsetY: true, closeOnClick: true, closeOnContentClick: true, auto: true, maxWidth: 400, maxHeight: 320 }"
            />
          </div>
        </v-col>
      </v-row>

      <v-row>
        <!-- Alarm Status Filter -->
        <v-col cols="12" md="6" lg="4">
          <div class="filter-field">
            <HaqSelect
              v-model="filters.alarmStatus"
              :items="alarmStatusOptions"
              item-text="text"
              item-value="value"
              label="Alarm Status"
              clearable
              hide-details
              :menu-props="{ bottom: true, offsetY: true, closeOnClick: true, closeOnContentClick: true, auto: true, maxWidth: 400 }"
            />
          </div>
        </v-col>
        
        <!-- Point Type Filter -->
        <v-col cols="12" md="6" lg="4">
          <div class="filter-field">
            <HaqSelect
              v-model="filters.pointType"
              :items="filteredPointTypes"
              label="Point Type"
              clearable
              hide-details
              :loading="loadingPointTypes"
              :menu-props="{ bottom: true, offsetY: true, closeOnClick: true, closeOnContentClick: true, auto: true, maxWidth: 400 }"
            />
          </div>
        </v-col>

        <!-- Name Filter -->
        <v-col cols="12" md="6" lg="4">
          <div class="filter-field">
              <HaqTextField
                v-model="filters.name"
                label="Name Contains"
                hide-details
                ref="nameContainsFilter"
              />
          </div>
        </v-col>
      </v-row>
      
      <!-- Action Buttons -->
      <v-row class="mt-2 action-buttons">
        <v-col>
          <v-btn
            @click="searchPoints"
            class="ml-2 haq-btn haq-btn-primary v-btn--is-elevated action-button search-button"
            :loading="searching"
            :disabled="!hasSite"
          >
            <v-icon left>mdi-magnify</v-icon>
            SEARCH
          </v-btn>
<!-- v-btn.haq-btn-primary.waves-effect.waves-light(:disabled="isDisabled" @click="updateNotifications") Update Notifications -->
          <v-btn
            @click="clearFilters"
            class="ml-2 haq-btn haq-btn-primary v-btn--is-elevated action-button search-button"
            :disabled="!hasFilters"
          >
            <v-icon left>mdi-filter-remove</v-icon>
            CLEAR ALL
          </v-btn>

          <v-btn
            @click="showSaveModal = true"
            class="ml-2 haq-btn haq-btn-primary v-btn--is-elevated action-button search-button"
            :disabled="!hasSite"
          >
            <v-icon left>mdi-content-save</v-icon>
            SAVE FILTERS
          </v-btn>
        </v-col>
      </v-row>
    </v-card-text>
    
    <!-- Save Query Modal -->
    <SaveQueryModal
      :dialog="showSaveModal"
      :filters="filters"
      @update:dialog="showSaveModal = $event"
      @save-query="handleSaveQuery"
    />
  </v-card>
</template>

<script>
import { HTTPClient } from '@/assets/js/http';
import { sitesUserCanAccess } from '@/assets/js/siteAccess';
import HaqAutocomplete from '@/components/form/HaqAutocomplete.vue';
import HaqSelect from '@/components/form/HaqSelect.vue';
import HaqTextField from '@/components/form/HaqTextField.vue';
import SaveQueryModal from './SaveQueryModal.vue';

let client = new HTTPClient();

export default {
  name: 'PointExplorerFilters',
  components: {
    HaqAutocomplete,
    HaqSelect,
    HaqTextField,
    SaveQueryModal
  },
  data() {
    return {
      // Filter values
      filters: {
        alarmStatus: null,
        pointType: null,
        siteId: null,
        zoneId: null,
        name: null
      },
      
      // Loading states
      loadingPointTypes: false,
      loadingSites: false,
      loadingZones: false,
      loadingSavedQueries: false,
      applying: false,
      searching: false,
      
      // Data arrays
      pointTypeOptions: [],
      siteOptions: [],
      zoneOptions: [],
      savedQueryOptions: [],
      
      // Search strings for filtering dropdown options
      alarmStatusSearch: '',
      pointTypeSearch: '',
      savedQuerySearch: '',
      
      // Saved query management
      selectedSavedQuery: null,
      showSaveModal: false
    };
  },
  computed: {
    // Alarm status options (static for now)
    alarmStatusOptions() {
      const options = [
        { text: 'Active', value: 'active' },
        { text: 'Not Active', value: 'not_active' }
      ];
      
      if (!this.alarmStatusSearch) return options;
      
      return options.filter(option => 
        option.text.toLowerCase().includes(this.alarmStatusSearch.toLowerCase())
      );
    },
    
    // Filtered point types based on search
    filteredPointTypes() {
      if (!this.pointTypeSearch) return this.pointTypeOptions;
      
      return this.pointTypeOptions.filter(type => 
        type.toLowerCase().includes(this.pointTypeSearch.toLowerCase())
      );
    },
    
    // Check if site is selected to enable search
    hasSite() {
      return this.filters.siteId !== null && this.filters.siteId !== '';
    },
    
    // Check if any filters are applied
    hasFilters() {
      return Object.values(this.filters).some(value => 
        value !== null && value !== ''
      );
    }
  },
  methods: {
    async fetchPointTypes() {
      try {
        this.loadingPointTypes = true;
        const result = await client.robustQuery('/api/querypoints/point/types');
        this.pointTypeOptions = result.results || [];
      } catch (error) {
        console.error('Failed to fetch point types:', error);
        // window.M.toast({ html: 'Failed to load point types' });
      } finally {
        this.loadingPointTypes = false;
      }
    },
    
    async fetchSites() {
      try {
        this.loadingSites = true;
        
        // Get sites from store or API
        let sites = this.$store.state.sites;
        if (!sites || sites.length === 0) {
          const res = await client.robustQuery('/api', { slim: true });
          const pid = this.$route.params.projectId || this.$store.state.project?.objectId;
          sites = sitesUserCanAccess(res.sites || [], this.$store.state.user, pid);
          this.$store.commit('setSites', sites);
        } else {
          const pid = this.$route.params.projectId || this.$store.state.project?.objectId;
          sites = sitesUserCanAccess(sites, this.$store.state.user, pid);
        }
        
        // Format for dropdown
        this.siteOptions = sites.map(site => ({
          objectId: site.objectId,
          name: site.name
        })).sort((a, b) => a.name.localeCompare(b.name));
        
      } catch (error) {
        console.error('Failed to fetch sites:', error);
        // window.M.toast({ html: 'Failed to load sites' });
      } finally {
        this.loadingSites = false;
      }
    },
    
    async fetchZones(siteId) {
      if (!siteId) {
        this.zoneOptions = [];
        return;
      }
      
      try {
        this.loadingZones = true;
        const result = await client.robustQuery('/api/zones', { siteId });
        
        this.zoneOptions = (result.results || []).map(zone => ({
          objectId: zone.objectId,
          name: zone.name
        })).sort((a, b) => a.name.localeCompare(b.name));
        
      } catch (error) {
        console.error('Failed to fetch zones:', error);
        // window.M.toast({ html: 'Failed to load zones' });
      } finally {
        this.loadingZones = false;
      }
    },
    
    async fetchSavedQueries() {
      try {
        this.loadingSavedQueries = true;
        const result = await client.robustQuery('/api/querypoints/queries');
        this.savedQueryOptions = result.results || [];
      } catch (error) {
        console.error('Failed to fetch saved queries:', error);
        // window.M.toast({ html: 'Failed to load saved queries' });
      } finally {
        this.loadingSavedQueries = false;
      }
    },
    

    
    async onSiteChange() {
      // Clear zone selection when site changes
      this.filters.zoneId = null;
      
      // Fetch zones for the selected site
      await this.fetchZones(this.filters.siteId);
      
      // Clear saved query selection when site changes
      this.selectedSavedQuery = null;
    },
    
    async onLoadQuery() {
      if (!this.selectedSavedQuery) return;
      
      try {
        // Find the selected query
        const query = this.savedQueryOptions.find(q => q.objectId === this.selectedSavedQuery);
        if (!query) return;
        
        // Apply the saved filters
        this.filters.alarmStatus = query.alarmStatus;
        this.filters.pointType = query.pointType;
        this.filters.siteId = query.site?.objectId || null;
        this.filters.zoneId = query.zone?.objectId || null;
        this.filters.name = query.nameFilter;
        
        // Fetch zones if a site is selected
        if (this.filters.siteId) {
          await this.fetchZones(this.filters.siteId);
        }
        
        // window.M.toast({ html: `Loaded query: ${query.name}` });
        
      } catch (error) {
        console.error('Failed to load query:', error);
        // window.M.toast({ html: 'Failed to load query' });
      }
    },
    
    async searchPoints() {
      try {
        this.searching = true;
        
        // Emit search event to parent
        this.$emit('search', { ...this.filters });
        
      } catch (error) {
        console.error('Failed to search points:', error);
        // window.M.toast({ html: 'Failed to search points' });
      } finally {
        this.searching = false;
      }
    },
    
    clearFilters() {
      this.filters = {
        alarmStatus: null,
        pointType: null,
        siteId: null,
        zoneId: null,
        name: null
      };
      
      this.selectedSavedQuery = null;
      this.zoneOptions = [];
      
      // Clear the search results when filters are cleared
      // this.$emit('search', { ...this.filters });
    },

    async handleSaveQuery(queryData) {
      try {
        // Save the query via API
        const result = await client.robustPost('/api/querypoints/queries', {
          name: queryData.name,
          alarmStatus: this.filters.alarmStatus,
          pointType: this.filters.pointType,
          siteId: this.filters.siteId,
          zoneId: this.filters.zoneId,
          nameFilter: this.filters.name
        });
        
        // Refresh saved queries list
        await this.fetchSavedQueries();
        
        // Emit success event to parent
        this.$emit('query-saved', result.results);
        
        // window.M.toast({ html: `Query "${queryData.name}" saved successfully!` });
        
      } catch (error) {
        console.error('Failed to save query:', error);
        // window.M.toast({ html: 'Failed to save query. Please try again.' });
      }
    }
  },
  watch: {
    loadingSites: {
      handler() {
        this.$nextTick(() => {
          console.log("refs", this.$refs);
          console.log("nameContainsFilter", this.$refs.nameContainsFilter);
          this.$refs.nameContainsFilter?.$el.querySelector("input")?.classList.add("browser-default");
        });
      },
      immediate: true
    }
  },
  async mounted() {
    // Load all initial data
    await Promise.all([
      this.fetchPointTypes(),
      this.fetchSites(),
      this.fetchSavedQueries()
    ]);

    // Add browser-default class to the input for Name Contains
    // this.$refs.nameContainsFilter?.$el.querySelector("input")?.classList.add("browser-default");
  }
};
</script>

<!-- .haq-text-field
  background: none !important
  border-radius: 12px !important -->

  <!-- ::v-deep .v-select__slot
  background: none !important -->

  <!-- v-input__slot
  var(--input-bg) -->
  
  <!-- input__append-inner {
    /* align-self: flex-start; -->

<style lang="sass" scoped>
.point-explorer-filters-card
  border-radius: 12px
  box-shadow: var(--box-shadow)
  background: var(--app-secondary-bg) !important
  
  .v-card-title
    background: var(--app-secondary-bg)
    color: var(--app-primary-txt)
    border-bottom: 1px solid var(--border)
    font-weight: 500
    
  .v-card-text
    padding: 24px
    
  .v-btn
    text-transform: none
    font-weight: 500
    
    &.v-btn--disabled
      opacity: 0.6
      
    &:not(.v-btn--disabled):hover
      transform: translateY(-1px)
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2)


.filter-field
  display: flex
  flex-direction: column
  width: 100%

// Dropdown search styling
.v-list-item
  &:hover
    background: var(--app-secondary-bg)

.action-buttons
  display: flex
  justify-content: flex-end
  align-items: center
  gap: 10px
  padding-top: 24px
  padding-right: 12px
  padding-bottom: 12px

  .action-button
    margin-right: 12px !important

.search-button
  background: #3456F7 !important
  color: #fff !important
</style> 